import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { ButtonsConfiguration, CollectionButtonConfiguration } from 'src/app/models/buttons-config';
import { Record } from 'src/app/models/record';
import { AuthService } from 'src/app/services/auth.service';
import { ApiService } from 'src/app/services/api.service';
import { style, animate, transition, trigger, state } from '@angular/animations';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { Subscription } from 'rxjs';
import { ResizeService } from 'src/app/services/resize.service';
import { Util } from 'src/app/util';
import { LocaleService } from 'src/app/services/locale.service';
import { ConfigService } from 'src/app/services/config.service';
import { Collection } from '../../models/collection';
import { SliderLoadedView } from '../../models/slider-loaded-view';
import { HomeFacade } from '../../state/home/home.facade';
import { UserListFacade } from '../../state/user-list/user-list.facade';
import { ActiveRecordFacade } from '../../state/core/active-record-facade';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FavoritesFacade } from 'src/app/state/user/favorites/favorites.facade';
import { ToastService } from '../../_core/services/toast.service';
import { UserFacade } from 'src/app/_core/state/user/user.facade';

@Component({
  selector: 'app-slider-item-info',
  templateUrl: './slider-item-info.component.html',
  styleUrls: ['./slider-item-info.component.scss'],
  animations: [
    trigger('desktopAnimation', [
      state(
        'void',
        style({
          height: 0,
          opacity: 0,
          'padding-top': '0',
          'padding-bottom': '0',
        })
      ),
      state(
        'visible',
        style({
          height: '*',
          opacity: 1,
          'padding-top': '32px',
          'padding-bottom': '32px',
        })
      ),
      transition('void <=> visible', animate('0.5s')),
    ]),
    trigger('mobileAnimation', [
      state(
        'void',
        style({
          height: 0,
          opacity: 0,
          'padding-top': '0',
          'padding-bottom': '0',
        })
      ),
      state(
        'visible',
        style({
          height: '*',
          opacity: 1,
          'padding-top': '24px',
          'padding-bottom': '24px',
        })
      ),
      transition('void <=> visible', animate('0.35s')),
    ]),
  ],
})
export class SliderItemInfoComponent implements OnInit, OnDestroy, OnChanges {
  private readonly toastService = inject(ToastService);
  private readonly userFacade = inject(UserFacade);

  @Input()
  record: Record;

  @Input()
  collection: Collection;

  @Input()
  loadedView: SliderLoadedView = 'HOME';

  @Input()
  selectedIndex: number;

  @Output()
  close = new EventEmitter<boolean>();

  @Input()
  forFavouritesSlider = false;

  @ViewChild('infoContainer', { static: false })
  private infoContainer: ElementRef<HTMLDivElement>;

  authService: AuthService;
  buttonsConfig: ButtonsConfiguration;
  sliderItemSub: Subscription;
  desktopAnimation: string;
  mobileAnimation: string;
  readOnly = true;
  issueDate: string;
  images: string[] = [];
  showSocialIcons: boolean;
  shareText: string;
  socialButtons: string[];
  showGallery: boolean;
  textDirection: string;
  toggleShowGallery: () => void;

  @ViewChild('accessibilityAutofocus') accessibilityAutofocus: ElementRef;
  readonly favoritesFacade = inject(FavoritesFacade);

  constructor(authService: AuthService,
              private configService: ConfigService,
              private broadcastService: BroadcastService,
              private apiService: ApiService,
              private localeService: LocaleService,
              private resizeService: ResizeService,
              private readonly homeFacade: HomeFacade,
              private readonly userListFacade: UserListFacade,
              private readonly breakpointObserver: BreakpointObserver) {

    this.authService = authService;
    this.showSocialIcons = configService.get().showSocialIcons;
    this.socialButtons = this.configService.shareIcons;
    this.buttonsConfig = new CollectionButtonConfiguration(configService.get().showReturnLoanButton);

    this.favoritesFacade.favoriteRemoved$.subscribe((fav) => {
      if (this.forFavouritesSlider && this.record && fav.recordId === this.record.id) {
        this.closeInfo();
      }
    });
  }

  ngOnInit() {
    this.textDirection = this.localeService.getTextDirection();
    this.sliderItemSub = this.broadcastService.sliderItem$.subscribe((item) => {
      if (!this.record) {
        return;
      }

      if (item.id !== this.record.id) {
        delete this.mobileAnimation;
        delete this.desktopAnimation;
        this.closeInfo();
      }
    });

    // Load previously selected record to open this component below the collection slider
    const activeRecord = this.getFacade().activeRecord;

    if (activeRecord && (this.collection?.id ?? 0) === activeRecord.collectionId) {
      this.record = { ...activeRecord };
      this.processRecord(this.record);

      this.getFacade().clearStore();
    }

    this.readOnly = !this.authService.isAuthenticated();
    this.toggleShowGallery = () => (this.showGallery = true);
  }

  ngOnDestroy() {
    this.sliderItemSub.unsubscribe();
    document.body.classList.remove('block-overflow');
  }

  ngOnChanges(changes: SimpleChanges) {
    const record = changes.record;

    if (record.isFirstChange() || !record.currentValue) {
      return;
    }

    this.processRecord(record.currentValue);
  }

  private getFacade(): ActiveRecordFacade {
    switch (this.loadedView) {
      case 'HOME':
        return this.homeFacade;
      case 'USER_LISTS':
        return this.userListFacade;
      default:
        return this.homeFacade;
    }
  }

  private processRecord(record: Record) {
    if (this.resizeService.isMobile) {
      document.body.classList.add('block-overflow');
      this.mobileAnimation = 'visible';
    } else {
      this.desktopAnimation = 'visible';
    }

    const recordId = record.id;
    const userId = this.authService.isAuthenticated() ? this.userFacade.user.id : null;

    if (!record.container) {
      this.apiService.getRecordAvailability(recordId, userId).subscribe((availability) => {
        this.record.availability = availability;
        this.record.availableIssueDates = availability.availableIssueDates;

        if (availability.availableIssueDates) {
          this.issueDate = Util.getLastIssueDate(this.record);
        }
      });
    }

    this.shareText = this.localeService.getLocalizedShareText(record, this.configService.get().title);
    this.getRecordImages();

    setTimeout(() => {
      this.accessibilityAutofocus?.nativeElement.focus();
      if (this.breakpointObserver.isMatched('max-width: 768px')) {
        this.infoContainer?.nativeElement.scrollTo({ top: 0 });
      } else {
        this.infoContainer?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 10);
  }

  handleAnimationEnd() {
    this.accessibilityAutofocus?.nativeElement.focus();
    this.infoContainer?.nativeElement.scrollTo({ top: 0 });
    if (this.breakpointObserver.isMatched('max-width: 768px')) {
      this.infoContainer?.nativeElement.scrollTo({ top: 0 });
    } else {
      this.infoContainer?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  saveActiveRecord() {
    this.getFacade().setActiveRecord(this.record, this.collection?.id ?? 0, this.selectedIndex);
  }

  getRecordImages() {
    this.images = [];

    if (this.record.resourceTypes.includes('TIPO_IMAGEN')) {
      this.apiService
        .getRecordInfo(this.record.id, 'IMAGE')
        .subscribe((images) => this.images.push(...images.map((i) => i.path)));
    }
  }

  searchByAuthor(author: string): { [p: string]: any } {
    return { query: `author:"${author}"` };
  }

  getRecordUrl(record: Record): string {
    return '/info/' + Util.slugifyRecord(record);
  }

  buildTopicFacetQuery(facet: string): object {
    return Util.buildTopicFacetQuery(facet);
  }

  closeInfo() {
    document.body.classList.remove('block-overflow');
    this.close.emit();
  }

  handleClick(event: string) {
    if (event === 'copy') {
      this.toastService.showToast('messages.copied', 'info');
    }
  }

  getSharingUrl(recordId: string) {
    return Util.getOrigin() + '/info/' + recordId;
  }
}
